import {
  Box,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { CustomInput, CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import PrescriptionSectionCard from './PrescriptionSectionCard'
import {
  DEFAULT_PRINT_FOOTER_SETTINGS,
  FOOTER_MODES,
  FOOTER_TYPES_ARRAY,
  PRINT_FOOTER_FIELD_IDS,
  PRINT_FOOTER_FIELD_OPTIONS,
  deriveFooterMode,
} from 'src/consumer/constants'
import { getEventTargetValueFromMuiToggleGroup } from 'src/consumer/helpers/utilFunctions'
import { getNumberStringForInput } from 'src/mentor/helpers/utilityFunctions'
import { getClinicDetailsColumnCount } from '../utils/prescriptionLayoutConfig'
import FooterFieldsRenderer from './FooterFieldsRenderer'
import RelatedClinicDetails from './RelatedClinicDetails'
import ImageUploadWithHeightAdjustment from '../ImageUploadWithHeightAdjustment'
import TemplatePlacementHelpDialog from './TemplatePlacementHelpDialog'

const MODE_ICONS = {
  [FOOTER_MODES.auto]: AutoAwesomeOutlinedIcon,
  [FOOTER_MODES.custom]: TextFieldsIcon,
  [FOOTER_MODES.image]: ImageOutlinedIcon,
}

/**
 * Footer configuration. The footer has three mutually-exclusive modes (footer.footerMode):
 *  - auto:   the auto-created footer built from the field toggles below
 *  - custom: hand-authored headline / subtext / support-text fields
 *  - image:  an uploaded footer image
 * For templates that predate footerMode we derive the mode so the selector reflects what
 * is actually rendered; picking a mode persists footer.footerMode on save.
 */
export default function FooterDetailsSection({
  prescriptionTemplate,
  handleChangeFooter,
  handleChangePrintFooterSettings,
  handleChangeRelatedClinicDetails,
  disabled,
  isUsingNewPrintMethod = false,
}) {
  const layout = prescriptionTemplate?.layout
  const footer = prescriptionTemplate?.footer
  const columnCount = getClinicDetailsColumnCount(layout)
  const printFooterSettings = footer?.printFooterSettings || {}

  const footerMode = footer?.footerMode || deriveFooterMode(footer)
  const isAutoFooter = footerMode === FOOTER_MODES.auto
  const isCustomFooter = footerMode === FOOTER_MODES.custom
  const isImageFooter = footerMode === FOOTER_MODES.image

  const handleFooterModeChange = (_, nextMode) => {
    if (!nextMode || disabled) {
      return
    }
    handleChangeFooter({ target: { id: 'footerMode', value: nextMode } })
  }

  const handleTogglePrintFooterField = (fieldId) => (event) => {
    if (!handleChangePrintFooterSettings) {
      return
    }
    handleChangePrintFooterSettings({ target: { id: fieldId, value: event.target.checked } })
  }

  const handleRegistrationNumberChange = (event) => {
    if (!handleChangePrintFooterSettings) {
      return
    }
    handleChangePrintFooterSettings({
      target: { id: 'registrationNumber', value: event.target.value },
    })
  }

  return (
    <PrescriptionSectionCard heading="Footer Details">
      <Grid item xs={12}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'flex-end' }}
          spacing={1.5}
        >
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <CustomSelect
              id="customFooterType"
              value={footer?.customFooterType}
              options={FOOTER_TYPES_ARRAY}
              onChangeEvent={handleChangeFooter}
              label="Footer placement"
              disabled={disabled}
              isClearable
              getOptionValue={(option) => option?.id}
              getOptionLabel={(option) => option?.title}
              selectType={selectTypes.string}
            />
          </Box>
          <TemplatePlacementHelpDialog
            label="Footer Type"
            kind="footer"
            options={FOOTER_TYPES_ARRAY}
          />
        </Stack>
      </Grid>

      {/* Three-way footer mode selector */}
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Footer type
        </Typography>
        <ToggleButtonGroup
          color="primary"
          size="small"
          value={footerMode}
          exclusive
          onChange={handleFooterModeChange}
          disabled={disabled}
          sx={{
            flexWrap: 'wrap',
            '& .MuiToggleButtonGroup-grouped': { borderRadius: '8px !important', border: '1px solid' },
            '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.75 },
          }}
        >
          {[FOOTER_MODES.auto, FOOTER_MODES.custom, FOOTER_MODES.image].map((modeId) => {
            const Icon = MODE_ICONS[modeId]
            const label =
              modeId === FOOTER_MODES.auto
                ? 'Auto-created'
                : modeId === FOOTER_MODES.custom
                  ? 'Create your own'
                  : 'Image footer'
            return (
              <ToggleButton key={modeId} value={modeId}>
                <Icon fontSize="small" sx={{ mr: 0.75 }} />
                {label}
              </ToggleButton>
            )
          })}
        </ToggleButtonGroup>
      </Grid>

      {/* IMAGE MODE */}
      {isImageFooter && (
        <Grid item xs={12} md={12}>
          <ImageUploadWithHeightAdjustment
            imageId={footer?.imageId}
            currentHeight={footer?.customFooterHeight}
            prescriptionTemplate={prescriptionTemplate}
            type="footer"
            defaultHeight={2}
            minHeight={0.5}
            maxHeight={5}
            showSlider={isUsingNewPrintMethod}
            onComplete={(imageId, adjustedHeight) => {
              handleChangeFooter({ target: { id: 'imageId', value: imageId } })
              handleChangeFooter({ target: { id: 'customFooterHeight', value: adjustedHeight } })
            }}
            disabled={disabled}
            placeholder="Add Footer Image"
          />
        </Grid>
      )}

      {/* Manual height applies to image and custom footers (the auto footer auto-sizes). */}
      {!isAutoFooter && (
        <Grid item xs={12} md={12}>
          <CustomInput
            id="customFooterHeight"
            label="Custom footer height in inches (manual adjustment)"
            type="number"
            value={getNumberStringForInput(footer?.customFooterHeight)}
            onChange={handleChangeFooter}
            disabled={disabled}
          />
        </Grid>
      )}

      {/* AUTO MODE — field toggles */}
      {isAutoFooter && (
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Footer fields
            </Typography>
            <Tooltip arrow title="Pick which items appear in the auto-created footer strip. Each is optional.">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
            </Tooltip>
          </Stack>
          <Stack spacing={0.25}>
            {PRINT_FOOTER_FIELD_OPTIONS.map((option) => {
              const checked =
                printFooterSettings?.[option.id] ?? DEFAULT_PRINT_FOOTER_SETTINGS[option.id] ?? false
              const isRegistration = option.id === PRINT_FOOTER_FIELD_IDS.showRegistrationNumber
              return (
                <Box key={option.id}>
                  <FormControlLabel
                    sx={{ mr: 0, alignItems: 'center' }}
                    control={
                      <Switch
                        size="small"
                        checked={Boolean(checked)}
                        onChange={handleTogglePrintFooterField(option.id)}
                        disabled={disabled || !handleChangePrintFooterSettings}
                      />
                    }
                    label={
                      <Stack>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.help}
                        </Typography>
                      </Stack>
                    }
                  />
                  {isRegistration && Boolean(checked) && (
                    <Box sx={{ pl: 6, pr: 1, pb: 0.5 }}>
                      <CustomInput
                        id="registrationNumber"
                        label="Registration number"
                        value={printFooterSettings?.registrationNumber || ''}
                        onChange={handleRegistrationNumberChange}
                        disabled={disabled || !handleChangePrintFooterSettings}
                      />
                    </Box>
                  )}
                </Box>
              )
            })}
          </Stack>
        </Grid>
      )}

      {/* CUSTOM MODE — hand-authored fields */}
      {isCustomFooter && (
        <>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Footer content
            </Typography>
            <ToggleButtonGroup
              color="primary"
              id="customFooterBlank"
              size="small"
              value={footer?.customFooterBlank}
              exclusive
              onChange={(e, v) => handleChangeFooter(getEventTargetValueFromMuiToggleGroup(false, e, v))}
              sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButtonGroup-grouped': { borderRadius: '8px !important' },
                '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.75 },
              }}
            >
              <ToggleButton id="customFooterBlank" value={true}>
                Show Blank Footer
              </ToggleButton>
              <ToggleButton id="customFooterBlank" value={false}>
                Show Filled Footer
              </ToggleButton>
              <ToggleButton id="customFooterBlank" value={null}>
                Use Default Values
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Related clinic details for multi-column footers */}
          {columnCount > 0 &&
            Array.from({ length: columnCount }).map((_, idx) => (
              <RelatedClinicDetails
                key={idx}
                clinicDetails={prescriptionTemplate?.relatedClinicDetails?.[idx]}
                idx={idx}
                layoutType={layout}
                handleChange={(e) => handleChangeRelatedClinicDetails(e, idx)}
              />
            ))}

          {/* Dynamic footer fields based on layout */}
          <FooterFieldsRenderer
            prescriptionTemplate={prescriptionTemplate}
            handleChangeFooter={handleChangeFooter}
            disabled={disabled}
          />
        </>
      )}
    </PrescriptionSectionCard>
  )
}
